import axios, {AxiosResponse} from "axios";
import {SpringPagedResult} from "@/service/PagedResult";
import {WorklogType} from "@/enums";

const PATH_V3 = "/v3/orders";
// The page size the worklogs are loaded with, the endpoint's default
const WORKLOG_PAGE_SIZE = 250;

export interface OrderOperatorUpdateRequestV3 {
  operatorNumber?: number;
  numberOfPositions?: number;
  qualityLevel?: string;
  valueOfGoods?: number;
}

export interface OrderOperatorCreateRequestV3 extends OrderOperatorUpdateRequestV3 {
  employeeId: string;
}

export interface OrderOperatorV3Dto {
  id: string;
  operatorNumber?: number;
  employee: {
    id: string;
    employeeNumber?: number;
    firstName: string;
    lastName: string;
  };
  numberOfPositions?: number;
  qualityLevel?: string;
  valueOfGoods?: number;
}

/**
 * One worklog of GET /v3/orders/{orderId}/worklogs. The employee is identified by its uuid, `start` and `end` are times
 * of day in the form `HH:mm` and `minutes` is the time between them.
 */
export interface OrderWorklogV3Dto {
  id: string;
  employee: {
    id: string;
    employeeNumber: number;
    firstName: string;
    lastName: string;
  };
  date: string;
  start: string;
  end: string;
  minutes: number;
  type: WorklogType;
  comment: string | null;
}

class OrderOperatorService {

  /**
   * Gets all work worklogs of an order via the v3 API by its uuid, ordered by day, start time and employee name,
   * loading every page of the paged endpoint.
   */
  getOrderWorkWorklogsV3 (orderUuid: string): Promise<Array<OrderWorklogV3Dto>> {
    return this.getOrderWorkWorklogsV3From(orderUuid, 0, []);
  }

  private getOrderWorkWorklogsV3From (orderUuid: string, page: number, worklogs: Array<OrderWorklogV3Dto>): Promise<Array<OrderWorklogV3Dto>> {
    return axios.get<SpringPagedResult<OrderWorklogV3Dto>>(`${PATH_V3}/${orderUuid}/worklogs`, {
      params: {
        worklogTypes: WorklogType.WORK,
        page: page,
        size: WORKLOG_PAGE_SIZE
      }
    }).then(response => {
      const loaded = worklogs.concat(response.data.content);
      return page + 1 < response.data.page.totalPages
        ? this.getOrderWorkWorklogsV3From(orderUuid, page + 1, loaded)
        : loaded;
    });
  }

  getOrderOperatorsV3 (orderUuid: string): Promise<AxiosResponse<{ operators: Array<OrderOperatorV3Dto> }>> {
    return axios.get(`${PATH_V3}/${orderUuid}/operators`);
  }

  createOrderOperatorV3 (orderUuid: string, data: OrderOperatorCreateRequestV3): Promise<AxiosResponse<OrderOperatorV3Dto>> {
    return axios.post(`${PATH_V3}/${orderUuid}/operators`, data, { headers: { "Content-Type": "application/json" } });
  }

  updateOrderOperatorV3 (orderUuid: string, operatorId: string, data: OrderOperatorUpdateRequestV3): Promise<AxiosResponse<OrderOperatorV3Dto>> {
    return axios.put(`${PATH_V3}/${orderUuid}/operators/${operatorId}`, data, { headers: { "Content-Type": "application/json" } });
  }
}

export default new OrderOperatorService();
